import React from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { Button, MenuItem, Alert, AlertTitle, Divider, Checkbox, FormControlLabel, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";

import { formatToPhoneNumber, getOnlyChangedValues } from "utils/formatFormFields";
import { COUNTRIES, STATES, TIMEZONES } from "@constants";
import { isSupervisor, isDirector, isAdmin, isSuperAdmin } from "selectors";
import { GET_CLIENTS, GET_TEAM_MEMBERS } from "@constants/reactQueryKeys";
import { useGlobalToast } from "components/GlobalToastProvider";
import {ANDatePicker} from "@answersnow/pkg-theme";
import { memberValidationSchema } from "../Profile/Member/validationSchema";
import { validateEmail } from "utils/validation";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { SimpleDeactivationDialog, CaregiverDeactivationDialog } from "elements/DeactivationDialogs";
import { useUserDeactivation } from "hooks/api/user/useUserDeactivation";
import { useGetClients } from "hooks/api/useGetClients";
import { updateTeamMembers } from "api/team";
import { getTeamMembers } from "api/team";
import { useDispatch } from "react-redux";
import actions from "actions";
import GuardianRequiredDialog from "../../Widgets/TeamSection/GuardianRequiredDialog";
import { RequiresDesignatedCaregiverDialog } from "./RequiresDesignatedCaregiverDialog";

interface EditTeamMemberProps {
  member: any;
  onClosePanel: () => void;
  onSubmit: (values: any) => Promise<any>;
  userPermissions: any;
  setEditMode?: (editMode: boolean) => void;
  setModePanel?: (mode: "edit" | "view") => void;
  showEditButton?: (show: boolean) => void;
  teamRoles?: any[];
  allTeamMembers?: any[];
}

export default function EditTeamMember({ 
  member, 
  onClosePanel, 
  onSubmit, 
  userPermissions, 
  setEditMode, 
  showEditButton, 
  teamRoles = [],
  allTeamMembers = []
}: EditTeamMemberProps) {
  const {
    firstName,
    lastName,
    dateOfBirth,
    gender,
    phoneNumber,
    email,
    address1,
    address2,
    country,
    state,
    city,
    zipCode,
    timezone,
    userId,
    documentSigner,
    roleId,
    legalGuardian,
  } = member;

  const fullName = `${firstName} ${lastName}`;

  const [legalGuardianDialogOpen, setLegalGuardianDialogOpen] = React.useState(false);
  const [designatedCaregiverDialogOpen, setDesignatedCaregiverDialogOpen] = React.useState(false);

  const legalGuardiansCount = React.useMemo(() => {
    return allTeamMembers.filter(
      (m) => m.hasAcceptedInvite && m.legalGuardian === true
    ).length;
  }, [allTeamMembers]);

  const designatedCaregiversCount = React.useMemo(() => {
    return allTeamMembers.filter(
      (m) => m.hasAcceptedInvite && m.documentSigner === true
    ).length;
  }, [allTeamMembers]);

  const currentUserIsSupervisor = useSelector(isSupervisor);
  const currentUserIsDirector = useSelector(isDirector);
  const currentUserIsAdmin = useSelector(isAdmin);
  const currentUserIsSuperAdmin = useSelector(isSuperAdmin);

  const { showToast } = useGlobalToast();

  const canDeactivateTeamMember =
    currentUserIsSupervisor || currentUserIsDirector || currentUserIsAdmin || currentUserIsSuperAdmin;

  const { clients } = useGetClients(
    userId ? { userIds: userId, role: "document_signer" } : null
  );
  const isActuallyDesignatedCaregiver = clients && clients.length > 0;

  const dispatch = useDispatch();

  const handleDeactivateWithReassignments = React.useCallback(
    async (reassignments: Map<number, number>) => {
      const reassignmentPromises = Array.from(reassignments.entries()).map(async ([teamId, newCaregiverId]) => {
        const teamMembersResponse = await getTeamMembers({ teamIds: teamId });
        const teamMembers = teamMembersResponse?.data?.data || teamMembersResponse?.data || [];

        if (!teamMembers || teamMembers.length === 0) {
          throw new Error(`No team members found for team ${teamId}`);
        }

        const acceptedMembers = teamMembers.filter((member: any) => member.hasAcceptedInvite);

        if (acceptedMembers.length === 0) {
          throw new Error(`No accepted team members found for team ${teamId}`);
        }

        const updatedTeamMembers = acceptedMembers.map((member: any) => ({
          id: member.id,
          roleId: member.roleId,
          legalGuardian: member.legalGuardian,
          documentSigner: member.userId === newCaregiverId,
        }));

        return updateTeamMembers(teamId, { teamMembers: updatedTeamMembers });
      });

      await Promise.all(reassignmentPromises);
      dispatch(actions.toggleUserActivation({ id: userId }));
    },
    [userId, dispatch]
  );

  const {
    simpleDeactivationDialogOpen,
    caregiverDeactivationDialogOpen,
    toggleUserActivationLoading,
    toggleUserActivationSuccess,
    handleOpenDeactivationDialog,
    handleCloseSimpleDeactivationDialog,
    handleCloseCaregiverDeactivationDialog,
    handleDeactivate,
  } = useUserDeactivation({
    userId,
    isDesignatedCaregiver: documentSigner,
    onSuccess: () => {
      showToast({
        message: `${fullName} has been successfully deactivated`,
        errorState: false,
      });
      if (setEditMode) {
        setEditMode(false);
      }
      if (showEditButton) {
        showEditButton(true);
      }
      onClosePanel();
    },
    onInvalidateCache: (queryClient) => {
      queryClient.invalidateQueries(GET_CLIENTS);
      queryClient.invalidateQueries(GET_TEAM_MEMBERS);
    },
  });

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isValid,
    isSubmitting,
    resetForm,
    touched,
    values,
    setErrors,
    setFieldTouched,
    initialValues,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: memberValidationSchema,
    initialValues: {
      relationshipType: roleId ?? "",
      isLegalGuardian: legalGuardian ?? false,
      isDesignatedCaregiver: documentSigner ?? false,
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      dateOfBirth: dateOfBirth ? new Date(dateWithoutTimezone(dateOfBirth)) : null,
      gender: gender ?? "",
      phoneNumber: phoneNumber ?? "",
      email: email ?? "",
      address1: address1 ?? "",
      address2: address2 ?? "",
      country: country ?? "",
      state: state ?? "",
      city: city ?? "",
      zipCode: zipCode ?? "",
      timezone: timezone ?? "",
    },
    onSubmit: (values) => {
      const changedValues = getOnlyChangedValues(initialValues, values);

      onSubmit(changedValues).finally(() => {
        resetForm();
      });
    },
  });

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClosePanel();
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value.replace(/[^\d]/g, "");
    handleChange({ target: { name: "phoneNumber", value: phone } });
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setFieldTouched("email", true);

    if (!validateEmail(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Invalid email address",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: undefined,
      }));
    }

    handleBlur(e);
  };

  const handleLegalGuardianChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    
    if (!isChecked && legalGuardiansCount <= 1) {
      setLegalGuardianDialogOpen(true);
      return;
    }
    
    handleChange(e);
  };

  const handleDesignatedCaregiverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    
    if (!isChecked && designatedCaregiversCount <= 1) {
      setDesignatedCaregiverDialogOpen(true);
      return;
    }
    
    handleChange(e);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container rowSpacing={7}>
        <Grid xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="isDesignatedCaregiver"
                checked={values.isDesignatedCaregiver}
                onChange={handleDesignatedCaregiverChange}
              />
            }
            label="Designated caregiver"
          />
          <Typography variant="caption" color="textSecondary">
            Caregivers get all the updates and messages while the clients are in care.
          </Typography>
        </Grid>

        <Grid xs={12}>
          <FormControlLabel
            control={
              <Checkbox 
                name="isLegalGuardian" 
                checked={values.isLegalGuardian}
                onChange={handleLegalGuardianChange}
              />
            }
            label="Legal guardian"
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            select
            fullWidth
            label="Relationship to client"
            name="relationshipType"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.relationshipType}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {teamRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="First name"
            name="firstName"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.firstName}
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="Last name"
            name="lastName"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lastName}
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
          />
        </Grid>

        <Grid xs={12}>
          <ANDatePicker
            label="Date of birth"
            format="MM/dd/yyyy"
            disableFuture
            style={{ fontSize: 14 }}
            name="dateOfBirth"
            value={values.dateOfBirth}
            onChange={(value) => {
              handleChange({ target: { name: "dateOfBirth", value } });
            }}
            onBlur={handleBlur}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "01/01/2023",
                error: touched.dateOfBirth && !!errors.dateOfBirth,
                helperText: touched.dateOfBirth && errors.dateOfBirth,
              },
              popper: {
                disablePortal: false,
                sx: { zIndex: 1400 },
              },
            }}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            select
            fullWidth
            label="Gender"
            name="gender"
            error={touched.gender && !!errors.gender}
            helperText={touched.gender && errors.gender}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.gender}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Non-binary">Non-binary</MenuItem>
            <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
            <MenuItem value="Unknown">-</MenuItem>
          </TextField>
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="Phone number"
            name="phoneNumber"
            size="small"
            onChange={handlePhoneNumberChange}
            onBlur={handleBlur}
            value={formatToPhoneNumber(values.phoneNumber)}
            error={touched.phoneNumber && !!errors.phoneNumber}
            helperText={touched.phoneNumber && errors.phoneNumber}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="Email address"
            name="email"
            size="small"
            onChange={handleChange}
            onBlur={handleEmailBlur}
            value={values.email}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            disabled={!userPermissions.update_client_email_address}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="Address 1"
            name="address1"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address1}
            error={touched.address1 && !!errors.address1}
            helperText={touched.address1 && errors.address1}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="Address 2"
            name="address2"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address2}
            error={touched.address2 && !!errors.address2}
            helperText={touched.address2 && errors.address2}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            select
            fullWidth
            label="Country"
            name="country"
            error={touched.country && !!errors.country}
            helperText={touched.country && errors.country}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.country}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {Object.entries(COUNTRIES).map(([key, value]) => {
              return (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid xs={12}>
          <TextField
            select
            fullWidth
            label="State"
            name="state"
            error={touched.state && !!errors.state}
            helperText={touched.state && errors.state}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.state}
            disabled={values.country !== "US"}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {Object.entries(STATES).map(([key, value]) => {
              return (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="City"
            name="city"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.city}
            error={touched.city && !!errors.city}
            helperText={touched.city && errors.city}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            fullWidth
            label="ZIP code"
            name="zipCode"
            size="small"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.zipCode}
            error={touched.zipCode && !!errors.zipCode}
            helperText={touched.zipCode && errors.zipCode}
          />
        </Grid>

        <Grid xs={12}>
          <TextField
            select
            fullWidth
            label="Time zone"
            name="timezone"
            error={touched.timezone && !!errors.timezone}
            helperText={touched.timezone && errors.timezone}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.timezone}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {Object.entries(TIMEZONES).map(([key, value]) => {
              return (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
      </Grid>

      {canDeactivateTeamMember && (
        <>
          <Divider sx={{ marginTop: 7, marginBottom: 3 }} />
          <Alert
            severity="error"
            action={
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={handleOpenDeactivationDialog}
              >
                Deactivate
              </Button>
            }
          >
            <AlertTitle>Deactivate Team Member</AlertTitle>
            Deactivation will suspend account access for the team member.
          </Alert>

          <SimpleDeactivationDialog
            open={simpleDeactivationDialogOpen}
            onClose={handleCloseSimpleDeactivationDialog}
            onConfirm={handleDeactivate}
            loading={toggleUserActivationLoading}
            success={toggleUserActivationSuccess}
          />

          <CaregiverDeactivationDialog
            open={caregiverDeactivationDialogOpen}
            onClose={handleCloseCaregiverDeactivationDialog}
            userId={userId}
            isDesignatedCaregiver={isActuallyDesignatedCaregiver}
            onDeactivate={handleDeactivateWithReassignments}
            deactivationLoading={toggleUserActivationLoading}
          />
        </>
      )}

      <Grid container justifyContent="flex-end" marginTop={7} rowSpacing={3}>
        <Grid xs={12}>
          <LoadingButton fullWidth type="submit" loading={isSubmitting} disabled={!isValid}>
            Save Changes
          </LoadingButton>
        </Grid>

        <Grid xs={12}>
          <Button
            fullWidth
            color="secondary"
            variant="text"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>

      <GuardianRequiredDialog
        open={legalGuardianDialogOpen}
        onClose={() => setLegalGuardianDialogOpen(false)}
      />

      <RequiresDesignatedCaregiverDialog
        open={designatedCaregiverDialogOpen}
        onClose={() => setDesignatedCaregiverDialogOpen(false)}
      />
    </form>
  );
}